import moment, { Moment } from 'moment';
import { BookerStatus } from './types';

export const renderBookerStatus = (status: BookerStatus) => {
    switch (status) {
        case 'active':
            return 'Aktiv';
        case 'inactive':
            return 'Inaktiv';
        case 'cancelled':
            return 'Abgebrochen';
        case 'rejected':
            return 'Abgelehnt';
        case 'waiting_for_payment':
            return 'Warten auf Zahlung';
        default:
            return null;
    }
};

export const renderTimeFromMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const renderMomentFromMinutes = (minutes: number, date?: Date): Moment => {
    return moment(date).local().startOf('day').add(minutes, 'minutes');
};

export const renderMinutesFromTime = (time: Date) => {
    return moment(time).diff(moment(time).startOf('day'), 'minutes');
};

/** Fallback maximum when the club has not configured a maximum booking length. */
export const DEFAULT_MAX_INTERVALS = 7;

export interface LengthOptionsArgs {
    /** Booking granularity in minutes — every option is a multiple of this */
    interval: number;
    /** Number of consecutive free intervals starting at the selected start time */
    availableIntervals: number;
    /** Club setting "space_manager.booking.min_length" in minutes */
    minLength?: number | string | null;
    /** Club setting "space_manager.booking.max_length" in minutes */
    maxLength?: number | string | null;
}

/**
 * Parses a settings value into a positive number of minutes, or undefined when unset/invalid.
 * Settings can arrive as strings from the API, so Number.isFinite alone is not enough.
 */
const parseMinutes = (value?: number | string | null) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * Builds the selectable booking lengths as interval counts (multiply by `interval` for minutes).
 *
 * The minimum rounds *up* and the maximum rounds *down*, so a length outside the club's configured
 * window is never offered when it is not an exact multiple of the interval — the backend enforces
 * the same window and would reject such a booking with a 403.
 * Returns an empty list when the settings and the free space leave no valid length.
 */
export const getLengthOptions = ({
    interval,
    availableIntervals,
    minLength,
    maxLength
}: LengthOptionsArgs): number[] => {
    if (!Number.isFinite(interval) || interval <= 0) return [];
    if (!Number.isFinite(availableIntervals) || availableIntervals <= 0) return [];

    const min = parseMinutes(minLength);
    const max = parseMinutes(maxLength);

    const minIntervals = min ? Math.max(1, Math.ceil(min / interval)) : 1;
    const maxIntervals = Math.min(
        availableIntervals,
        max ? Math.floor(max / interval) : DEFAULT_MAX_INTERVALS
    );

    if (maxIntervals < minIntervals) return [];

    return Array.from({ length: maxIntervals - minIntervals + 1 }, (_, index) => minIntervals + index);
};
